import { useState, useEffect, useMemo, useRef, useCallback, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Calendar, Bell, CheckCircle, Smile, Compass,
  ChevronRight, Settings, Loader2, ArrowRight,
  ClipboardList, BookOpen, StickyNote, Utensils, Heart,
  Target, PiggyBank, Sparkles, Lightbulb, ChevronDown,
  Mic, Square, MicOff, Plus, Trash2, Clock, Play, Check, X
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getRandomTip } from "@/data/dailyTips";
import { selfDiscoveryPhrase, readyToDiscoverPhrase } from "@/lib/gender";
import StructuredCheckIn from "@/components/voicemood/StructuredCheckIn";
import SmartVoiceCommand from "@/components/voicemood/SmartVoiceCommand";
import { playSaveChime, playMicStart, playMicStop, playError } from "@/lib/sound";

const ADMIN_EMAIL = "piccinini.ps@gmail.com";

// Lazy-loaded full-page components for single-focus expanded view on Home.
// Keeps Oggi bundle small while allowing the user to see an entire area inline.
const FULL_PAGE_COMPONENTS = {
  tasks: lazy(() => import("@/pages/LifeTasksPage")),
  calendar: lazy(() => import("@/pages/CalendarPage")),
  expenses: lazy(() => import("@/pages/ExpensesPage")),
  notes: lazy(() => import("@/pages/NotesPage")),
  mood: lazy(() => import("@/pages/MoodPage")),
  habits: lazy(() => import("@/pages/HabitsPage")),
  diary: lazy(() => import("@/pages/DiaryPage")),
  nutrition: lazy(() => import("@/pages/NutritionPage")),
  needs: lazy(() => import("@/pages/NeedsPage")),
};

// ===== FOCUS OPTIONS CONFIG =====
const ALL_FOCUS_OPTIONS = [
  { id: "tasks", label: "Compiti", category: "Organizza", icon: ClipboardList, gradient: "from-blue-500 to-indigo-600", bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-600", link: "/organizza?section=compiti" },
  { id: "calendar", label: "Calendario", category: "Organizza", icon: Calendar, gradient: "from-purple-500 to-violet-600", bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-600", link: "/organizza?section=calendario" },
  { id: "expenses", label: "Spese", category: "Organizza", icon: PiggyBank, gradient: "from-emerald-500 to-teal-600", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-600", link: "/organizza?section=spese" },
  { id: "notes", label: "Note", category: "Organizza", icon: StickyNote, gradient: "from-amber-500 to-orange-600", bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-600", link: "/organizza?section=note" },
  { id: "mood", label: "Umore", category: "Benessere", icon: Smile, gradient: "from-yellow-400 to-amber-500", bg: "bg-yellow-50", border: "border-yellow-200", text: "text-yellow-600", link: "/benessere?section=umore" },
  { id: "habits", label: "Abitudini", category: "Benessere", icon: Target, gradient: "from-orange-500 to-red-500", bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-600", link: "/benessere?section=abitudini" },
  { id: "diary", label: "Diario", category: "Benessere", icon: BookOpen, gradient: "from-cyan-500 to-blue-500", bg: "bg-cyan-50", border: "border-cyan-200", text: "text-cyan-600", link: "/benessere?section=diario" },
  // Nutrition tile removed from main focus options (2026-07-10): il tracker
  // nutrizionale non è core rispetto alla missione psicologica di Revela.
  // Rimane raggiungibile via `/benessere?section=nutrizione` (link in Impostazioni → Extra).
  { id: "needs", label: "Bisogni", category: "Benessere", icon: Heart, gradient: "from-rose-500 to-pink-600", bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-600", link: "/benessere?section=bisogni" },
  { id: "journey", label: "Percorso", category: "Percorsi", icon: Compass, gradient: "from-indigo-500 to-purple-600", bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-600", link: "/percorsi" },
];

const FOCUS_PREF_KEY = "oggi_focus_selection";
const MAX_FOCUS_ITEMS = 3;
const DEFAULT_FOCUS = ["tasks", "mood", "calendar"];

const MOOD_LEVELS = [
  { level: 1, emoji: "\u{1F622}", label: "Pessimo" },
  { level: 2, emoji: "\u{1F615}", label: "Male" },
  { level: 3, emoji: "\u{1F610}", label: "Cos\u00ec cos\u00ec" },
  { level: 4, emoji: "\u{1F642}", label: "Bene" },
  { level: 5, emoji: "\u{1F604}", label: "Ottimo" },
];

// ===== FOCUS WIDGET COMPONENTS =====

const TasksWidget = ({ data, config, onRefresh }) => {
  const d = data?.tasks;
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [title, setTitle] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (showQuickAdd && inputRef.current) inputRef.current.focus();
  }, [showQuickAdd]);

  const handleAdd = async (e) => {
    e?.preventDefault();
    const t = title.trim();
    if (!t) return;
    setSaving(true);
    try {
      await api.post("/life-tasks", {
        title: t,
        domain: "altro",
        is_urgent: true,
        is_priority: false,
      });
      toast.success("Urgenza aggiunta");
      setTitle("");
      setShowQuickAdd(false);
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'aggiunta");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setShowQuickAdd(false);
    setTitle("");
  };

  return (
    <WidgetShell
      config={config}
      subtitle={`${d?.pending_count || 0} task attivi`}
      extraAction={
        <button
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowQuickAdd((v) => !v); }}
          className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 active:scale-95 transition-all shadow-sm"
          data-testid="tasks-quickadd-toggle"
          aria-label="Aggiungi urgenza"
          title="Aggiungi urgenza rapida"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      }
    >
      {showQuickAdd && (
        <form onSubmit={handleAdd} className="mb-3 space-y-2" data-testid="tasks-quickadd-form">
          <div className="flex gap-1.5">
            <input
              ref={inputRef}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Es. Chiamare il dottore entro oggi"
              maxLength={200}
              className="flex-1 text-sm px-2.5 py-1.5 rounded-lg border border-red-200 bg-red-50/30 focus:outline-none focus:ring-2 focus:ring-red-300 focus:border-red-400"
              data-testid="tasks-quickadd-input"
            />
            <Button
              type="submit"
              disabled={saving || !title.trim()}
              size="sm"
              className="h-8 px-2.5 bg-red-500 hover:bg-red-600 text-white"
              data-testid="tasks-quickadd-save"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            </Button>
            <Button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              size="sm"
              variant="ghost"
              className="h-8 px-2 text-muted-foreground"
              data-testid="tasks-quickadd-cancel"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Salvata come urgenza senza categoria (finisce in "Altro"). Potrai riorganizzarla dopo.
          </p>
        </form>
      )}

      {d?.urgent?.length > 0 && (
        <div className="space-y-1.5 mb-2">
          <p className="text-xs font-semibold text-red-600 uppercase tracking-wide">Urgenti</p>
          {d.urgent.map((t, i) => (
            <div key={`urgent-${t.title}-${i}`} className="flex items-center gap-2 text-sm p-1.5 rounded bg-red-50/70">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
              <span className="truncate">{t.title}</span>
            </div>
          ))}
        </div>
      )}
      {d?.priorities?.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide">Priorita</p>
          {d.priorities.map((t, i) => (
            <div key={`prio-${t.title}-${i}`} className="flex items-center gap-2 text-sm p-1.5 rounded bg-amber-50/70">
              <Sparkles className="w-3 h-3 text-amber-500 shrink-0" />
              <span className="truncate">{t.title}</span>
            </div>
          ))}
        </div>
      )}
      {!d?.urgent?.length && !d?.priorities?.length && !showQuickAdd && (
        <p className="text-sm text-muted-foreground">Nessun task urgente o prioritario</p>
      )}
    </WidgetShell>
  );
};

const CalendarWidget = ({ data, config }) => {
  const d = data?.calendar;
  const hasEvents = (d?.appointments?.length || 0) + (d?.reminders?.length || 0) > 0;
  return (
    <WidgetShell config={config} subtitle="In programma oggi">
      {!hasEvents ? (
        <p className="text-sm text-muted-foreground">Nessun impegno per oggi</p>
      ) : (
        <div className="space-y-1.5">
          {d?.appointments?.map((a) => (
            <div key={a.appointment_id || a.title} className="flex items-center gap-2 text-sm p-1.5 rounded bg-purple-50/70">
              <Calendar className="w-3.5 h-3.5 text-purple-500 shrink-0" />
              <span className="truncate flex-1">{a.title}</span>
              {a.time && <span className="text-xs text-muted-foreground shrink-0">{a.time}</span>}
            </div>
          ))}
          {d?.reminders?.map((r) => (
            <div key={r.datetime_iso || r.title} className="flex items-center gap-2 text-sm p-1.5 rounded bg-amber-50/70">
              <Bell className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              <span className="truncate">{r.title}</span>
            </div>
          ))}
        </div>
      )}
    </WidgetShell>
  );
};

const ExpensesWidget = ({ data, config }) => {
  const d = data?.expenses;
  return (
    <WidgetShell config={config} subtitle={`Mese corrente`}>
      <div className="flex items-baseline gap-1 mb-2">
        <span className="text-2xl font-bold">{d?.month_total?.toFixed(2) || "0.00"}</span>
        <span className="text-sm text-muted-foreground">EUR</span>
      </div>
      <div className="flex gap-2">
        {["I", "A", "X"].map(cat => (
          <div key={cat} className="flex-1 text-center p-1.5 rounded bg-muted/50">
            <p className="text-xs text-muted-foreground">{cat === "I" ? "Indispensabili" : cat === "A" ? "Accessorie" : "Imprevisti"}</p>
            <p className="text-sm font-semibold">{(d?.by_category?.[cat] || 0).toFixed(0)}</p>
          </div>
        ))}
      </div>
    </WidgetShell>
  );
};

const NotesWidget = ({ data, config }) => {
  const d = data?.notes;
  return (
    <WidgetShell config={config} subtitle="Note recenti">
      {d?.recent?.length > 0 ? (
        <div className="space-y-1.5">
          {d.recent.map((n) => (
            <div key={n.note_id || n.title} className="flex items-center gap-2 text-sm p-1.5 rounded bg-amber-50/70">
              <StickyNote className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              <span className="truncate">{n.title || "Senza titolo"}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Nessuna nota salvata</p>
      )}
    </WidgetShell>
  );
};

const MoodWidget = ({ data, config, onMoodSubmit, moodSubmitting }) => {
  const d = data?.mood;
  return (
    <WidgetShell config={config} subtitle="Come ti senti oggi?">
      {d?.today ? (
        <div className="flex items-center gap-3 bg-muted/50 rounded-lg p-3">
          <span className="text-3xl">{MOOD_LEVELS[d.today.level - 1]?.emoji}</span>
          <div>
            <p className="font-medium text-sm">{MOOD_LEVELS[d.today.level - 1]?.label}</p>
            <p className="text-xs text-muted-foreground">Registrato oggi</p>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex justify-between">
            {MOOD_LEVELS.map(({ level, emoji, label }) => (
              <button
                key={level}
                onClick={() => onMoodSubmit(level)}
                disabled={moodSubmitting}
                className="flex flex-col items-center p-2 rounded-lg hover:bg-muted transition-colors"
                title={label}
                data-testid={`mood-btn-${level}`}
              >
                <span className="text-2xl">{emoji}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-center text-muted-foreground">Tocca per registrare</p>
        </div>
      )}
    </WidgetShell>
  );
};

const HabitsWidget = ({ data, config }) => {
  const d = data?.habits;
  return (
    <WidgetShell config={config} subtitle={`${d?.done_today || 0}/${d?.total || 0} completate oggi`}>
      {d?.habits_list?.length > 0 ? (
        <div className="space-y-1.5">
          {d.habits_list.map((h) => (
            <div key={h.habit_id || h.name} className={`flex items-center gap-2 text-sm p-1.5 rounded ${h.done ? "bg-green-50/70" : "bg-muted/40"}`}>
              <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${h.done ? "bg-green-500" : "border-2 border-gray-300"}`}>
                {h.done && <CheckCircle className="w-2.5 h-2.5 text-white" />}
              </div>
              <span className={`truncate ${h.done ? "line-through text-muted-foreground" : ""}`}>{h.name}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Nessuna abitudine configurata</p>
      )}
    </WidgetShell>
  );
};

const DiaryWidget = ({ data, config }) => {
  const d = data?.diary;
  return (
    <WidgetShell config={config} subtitle="Il tuo diario">
      {d?.has_entry_today ? (
        <div className="flex items-center gap-3 bg-cyan-50/70 rounded-lg p-3">
          <CheckCircle className="w-5 h-5 text-cyan-600" />
          <p className="text-sm font-medium">Hai scritto nel diario oggi</p>
        </div>
      ) : (
        <div className="text-center py-2">
          <BookOpen className="w-8 h-8 text-muted-foreground/40 mx-auto mb-1" />
          <p className="text-sm text-muted-foreground">Non hai ancora scritto oggi</p>
        </div>
      )}
    </WidgetShell>
  );
};

const NutritionWidget = ({ data, config }) => {
  const d = data?.nutrition;
  return (
    <WidgetShell config={config} subtitle="Oggi">
      <div className="flex gap-4">
        <div className="text-center flex-1 p-2 rounded bg-green-50/70">
          <p className="text-2xl font-bold">{d?.today_calories || 0}</p>
          <p className="text-xs text-muted-foreground">kcal</p>
        </div>
        <div className="text-center flex-1 p-2 rounded bg-green-50/70">
          <p className="text-2xl font-bold">{d?.today_meals || 0}</p>
          <p className="text-xs text-muted-foreground">pasti</p>
        </div>
      </div>
    </WidgetShell>
  );
};

const NeedsWidget = ({ data, config }) => {
  const d = data?.needs;
  return (
    <WidgetShell config={config} subtitle="Ruota dei bisogni">
      {d ? (
        <div className="flex items-center gap-3 bg-rose-50/70 rounded-lg p-3">
          <div className="text-center">
            <p className="text-2xl font-bold text-rose-600">{d.satisfied_count}/{d.total}</p>
            <p className="text-xs text-muted-foreground">soddisfatti</p>
          </div>
        </div>
      ) : (
        <div className="text-center py-2">
          <Heart className="w-8 h-8 text-muted-foreground/40 mx-auto mb-1" />
          <p className="text-sm text-muted-foreground">Nessun check-in oggi</p>
        </div>
      )}
    </WidgetShell>
  );
};

const JourneyWidget = ({ data, config }) => {
  const d = data?.journey;
  return (
    <WidgetShell config={config} subtitle="I tuoi percorsi">
      {d?.is_active ? (
        <div className="flex items-center gap-3 bg-indigo-50/70 rounded-lg p-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0">
            <Target className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-sm">Training Assertivita</p>
            <p className="text-xs text-muted-foreground">Abbonamento attivo</p>
          </div>
        </div>
      ) : (
        <div className="text-center py-2">
          <Compass className="w-8 h-8 text-muted-foreground/40 mx-auto mb-1" />
          <p className="text-sm text-muted-foreground">Scopri i percorsi guidati</p>
        </div>
      )}
    </WidgetShell>
  );
};

// ===== WIDGET SHELL =====
const WidgetShell = ({ config, subtitle, children, extraAction }) => {
  const Icon = config.icon;
  return (
    <Card className={`border shadow-sm hover:shadow-md transition-all ${config.border}`} data-testid={`focus-widget-${config.id}`}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center justify-between mb-2 sm:mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br ${config.gradient} flex items-center justify-center shrink-0`}>
              <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-xs sm:text-sm">{config.label}</h3>
              {subtitle && <p className="text-[10px] sm:text-xs text-muted-foreground">{subtitle}</p>}
            </div>
          </div>
          <div className="flex items-center gap-0.5 -mr-1">
            {extraAction || (
              <Link to={config.link}>
                <Button variant="ghost" size="sm" className={`${config.text} h-7 w-7 p-0`} title="Aggiungi / Modifica" data-testid={`widget-edit-${config.id}`}>
                  <Plus className="w-4 h-4" />
                </Button>
              </Link>
            )}
            <Link to={config.link}>
              <Button variant="ghost" size="sm" className={`text-xs ${config.text} h-7 px-1.5`} data-testid={`widget-goto-${config.id}`}>
                Vai <ChevronRight className="w-3 h-3 ml-0.5" />
              </Button>
            </Link>
          </div>
        </div>
        {children}
      </CardContent>
    </Card>
  );
};

// Widget rendering map
const WIDGET_MAP = {
  tasks: TasksWidget,
  calendar: CalendarWidget,
  expenses: ExpensesWidget,
  notes: NotesWidget,
  mood: MoodWidget,
  habits: HabitsWidget,
  diary: DiaryWidget,
  nutrition: NutritionWidget,
  needs: NeedsWidget,
  journey: JourneyWidget,
};

// ===== FOCUS SELECTION DIALOG =====
const FocusSelectionDialog = ({ open, onOpenChange, selectedFocus, onToggle }) => {
  const grouped = useMemo(() => {
    const groups = {};
    ALL_FOCUS_OPTIONS.forEach(opt => {
      if (!groups[opt.category]) groups[opt.category] = [];
      groups[opt.category].push(opt);
    });
    return groups;
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm max-h-[80vh] overflow-y-auto" data-testid="focus-selection-dialog">
        <DialogHeader>
          <DialogTitle>Scegli i tuoi Focus</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">
          Seleziona fino a {MAX_FOCUS_ITEMS} moduli da mostrare nella tua Home.
        </p>
        <div className="space-y-4 pt-2">
          {Object.entries(grouped).map(([category, options]) => (
            <div key={category}>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">{category}</p>
              <div className="space-y-1.5">
                {options.map(opt => {
                  const isSelected = selectedFocus.includes(opt.id);
                  const isFull = selectedFocus.length >= MAX_FOCUS_ITEMS && !isSelected;
                  const Icon = opt.icon;
                  return (
                    <label
                      key={opt.id}
                      className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                        isSelected ? `${opt.bg} ${opt.border}` : isFull ? "opacity-50 cursor-not-allowed border-border" : "hover:bg-muted/50 border-border"
                      }`}
                      data-testid={`focus-option-${opt.id}`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => onToggle(opt.id)}
                        disabled={isFull}
                      />
                      <div className={`w-7 h-7 rounded-md bg-gradient-to-br ${opt.gradient} flex items-center justify-center shrink-0`}>
                        <Icon className="w-3.5 h-3.5 text-white" />
                      </div>
                      <span className={`text-sm ${isSelected ? "font-medium" : ""}`}>{opt.label}</span>
                      {isSelected && <CheckCircle className={`w-4 h-4 ml-auto ${opt.text}`} />}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="pt-2">
          <Badge variant="secondary" className="text-xs">
            {selectedFocus.length}/{MAX_FOCUS_ITEMS} selezionati
          </Badge>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ===== TIP (rotating, silver bg, no heading) =====
// Picked randomly on each mount so it changes every time the user enters the app.
const TipOfTheDay = () => {
  const [showRef, setShowRef] = useState(false);
  const tip = useMemo(() => getRandomTip(), []);

  return (
    <div
      className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-lg p-4 sm:p-5"
      data-testid="tip-of-the-day"
    >
      <p className="text-sm sm:text-[15px] leading-relaxed text-slate-700 font-light italic">
        {tip.text}
      </p>
      <button
        onClick={() => setShowRef(!showRef)}
        className="flex items-center gap-1 mt-3 text-[11px] text-slate-500 hover:text-slate-700 transition-colors font-light tracking-wide"
        data-testid="tip-reference-toggle"
      >
        <BookOpen className="w-3 h-3" strokeWidth={1.5} />
        Riferimenti scientifici
        <ChevronDown className={`w-3 h-3 transition-transform ${showRef ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>
      {showRef && (
        <div className="mt-2 pt-2 border-t border-slate-200" data-testid="tip-reference-content">
          <p className="text-xs text-slate-500 leading-relaxed italic font-light">{tip.reference}</p>
        </div>
      )}
    </div>
  );
};

// ===== HOME CHECK-IN LAUNCHER (admin only) =====
// Opens the structured EMA check-in wizard in a dialog. Teal theme.
const HomeCheckInLauncher = () => {
  const [open, setOpen] = useState(false);
  // 2026-05-13: distinguish how the wizard was opened so we tag the entry correctly:
  //   - "self_initiated" → user tapped the "+ Nuovo" button (spontaneous, frame=now)
  //   - null             → arrived from push notification (?open-checkin=1); the wizard
  //                        will infer sampling_type from the pending-prompt API.
  const [launchSamplingType, setLaunchSamplingType] = useState("self_initiated");
  // 2026-05-13: show the "spontaneous check-in" launcher only when the user has
  // enabled it in EMA settings (default: enabled for existing users).
  const [allowSelfInitiated, setAllowSelfInitiated] = useState(true);

  // Read schedule preferences once
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/voice-mood/schedule");
        if (typeof res.data?.allow_self_initiated === "boolean") {
          setAllowSelfInitiated(res.data.allow_self_initiated);
        }
      } catch {
        /* ignore — keep default */
      }
    })();
  }, []);

  // Auto-open when arriving from a push notification (?open-checkin=1)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("open-checkin") === "1") {
      setLaunchSamplingType(null); // backend will bind to pending prompt
      setOpen(true);
      // Clean URL so refresh doesn't re-open
      const url = new URL(window.location.href);
      url.searchParams.delete("open-checkin");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const openManual = () => {
    setLaunchSamplingType("self_initiated");
    setOpen(true);
  };

  if (!allowSelfInitiated) {
    // Still render the dialog (for push-opened flow) but hide the manual launcher card
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md p-0 max-h-[90vh] overflow-y-auto" data-testid="home-checkin-dialog">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle>Check-in · descrivi questo momento</DialogTitle>
          </DialogHeader>
          <div className="p-3">
            <StructuredCheckIn
              samplingType={launchSamplingType}
              onComplete={() => {
                setOpen(false);
                toast.success("Check-in salvato!");
              }}
              onCancel={() => setOpen(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Card className="border border-slate-300 bg-white shadow-sm hover:shadow-md transition-shadow" data-testid="home-checkin-launcher">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center gap-3">
            <button
              onClick={openManual}
              className="flex items-center gap-4 flex-1 min-w-0 active:scale-[0.99] transition-transform text-left"
              data-testid="home-checkin-btn"
            >
              <div className="w-14 h-14 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0">
                <Plus className="w-6 h-6 text-slate-600" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-3xl sm:text-4xl text-slate-800 leading-tight font-light">Check-in</p>
                <p className="text-sm text-slate-500 mt-1 font-light tracking-wide">Descrivi questo momento, scopri te stesso</p>
              </div>
            </button>
            <Link
              to="/benessere?section=checkin-benessere"
              className="flex items-center gap-1 px-3 py-2 rounded-full border border-slate-200 bg-white text-xs font-light text-slate-600 hover:bg-slate-50 hover:border-slate-300 shrink-0 transition-colors"
              data-testid="home-checkin-history-btn"
              title="Vedi storico check-in"
            >
              <Clock className="w-4 h-4" strokeWidth={1.5} />
              <span className="hidden sm:inline">Storico</span>
            </Link>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md p-0 max-h-[90vh] overflow-y-auto" data-testid="home-checkin-dialog">
          <DialogHeader className="p-4 pb-0">
            <DialogTitle>Check-in · descrivi questo momento</DialogTitle>
          </DialogHeader>
          <div className="p-3">
            <StructuredCheckIn
              samplingType={launchSamplingType}
              onComplete={() => {
                setOpen(false);
                toast.success("Check-in salvato!");
              }}
              onCancel={() => setOpen(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ===== HOME VOICE RECORDER (admin only) =====
// ===== VOICE NOTE AUDIO PLAYER =====
// Lazy-loaded audio blob (fetched via authenticated axios → Blob URL).
const VoiceNoteAudio = ({ noteId, url, loading, onLoad }) => {
  if (url) {
    return (
      <audio
        controls
        src={url}
        className="w-full h-8"
        style={{ minHeight: 32 }}
        data-testid={`voicenote-audio-${noteId}`}
      >
        Il tuo browser non supporta l'audio.
      </audio>
    );
  }
  return (
    <button
      onClick={onLoad}
      disabled={loading}
      className="inline-flex items-center gap-1.5 text-[11px] text-rose-600 hover:text-rose-800 disabled:opacity-50"
      data-testid={`voicenote-loadaudio-${noteId}`}
    >
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
      {loading ? "Carico..." : "Riascolta audio"}
    </button>
  );
};

// ===== HOME VOICE RECORDER (admin only) =====
// Compact icon button that opens a dialog with the free-form recorder.
// Used as a quick promemoria — transcribes via Whisper and stores in voice_notes collection.
// NOT fed into EMA correlations.
const HomeVoiceRecorder = () => {
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [lastResult, setLastResult] = useState(null); // { note_id, transcript, recorded_at }
  const [notes, setNotes] = useState([]);
  const [showArchive, setShowArchive] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [audioUrls, setAudioUrls] = useState({}); // note_id -> blob URL
  const [loadingAudio, setLoadingAudio] = useState({}); // note_id -> bool
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);

  const loadNotes = useCallback(async () => {
    setLoadingNotes(true);
    try {
      const res = await api.get("/voice-notes?days=30");
      setNotes(res.data || []);
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[VoiceNotes] load failed (non-critical):", e?.message);
      }
    } finally {
      setLoadingNotes(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadNotes();
  }, [open, loadNotes]);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      mediaRef.current = mr;
      chunksRef.current = [];
      cancelledRef.current = false;
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (cancelledRef.current) return;
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setProcessing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "note.webm");
          const res = await api.post("/voice-notes", fd, { headers: { "Content-Type": "multipart/form-data" } });
          playSaveChime();
          setLastResult(res.data);
          toast.success("Nota vocale trascritta ✓");
          // refresh archive
          loadNotes();
        } catch (err) {
          playError();
          toast.error(err.response?.data?.detail || "Errore nella trascrizione");
        } finally {
          setProcessing(false);
        }
      };
      mr.start(250);
      playMicStart();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(p => p + 1), 1000);
    } catch {
      toast.error("Impossibile accedere al microfono");
    }
  };

  const stopRec = () => {
    if (mediaRef.current?.state !== "inactive") mediaRef.current?.stop();
    clearInterval(timerRef.current);
    setRecording(false);
    playMicStop();
  };

  const cancelRec = () => {
    cancelledRef.current = true;
    if (mediaRef.current?.state !== "inactive") mediaRef.current?.stop();
    clearInterval(timerRef.current);
    setRecording(false);
    toast("Registrazione cestinata");
  };

  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/voice-notes/${noteId}`);
      setNotes((prev) => prev.filter((n) => n.note_id !== noteId));
      if (lastResult?.note_id === noteId) setLastResult(null);
      toast.success("Nota eliminata");
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  // Lazy-load audio blob as a browser URL the first time the user expands it.
  const loadAudioFor = useCallback(async (noteId) => {
    if (audioUrls[noteId] || loadingAudio[noteId]) return;
    setLoadingAudio((p) => ({ ...p, [noteId]: true }));
    try {
      const res = await api.get(`/voice-notes/${noteId}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setAudioUrls((p) => ({ ...p, [noteId]: url }));
    } catch {
      toast.error("Impossibile caricare l'audio");
    } finally {
      setLoadingAudio((p) => ({ ...p, [noteId]: false }));
    }
  }, [audioUrls, loadingAudio]);

  // Cleanup blob URLs when dialog closes / component unmounts
  useEffect(() => {
    return () => {
      Object.values(audioUrls).forEach((url) => {
        try { URL.revokeObjectURL(url); } catch { /* ignore */ }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => clearInterval(timerRef.current), []);

  // Stop & release mic if dialog is closed while recording
  useEffect(() => {
    if (!open && recording) {
      cancelRec();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const formatNoteTime = (iso) => {
    try {
      const d = new Date(iso);
      const today = new Date();
      const isToday = d.toDateString() === today.toDateString();
      const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
      if (isToday) return `Oggi · ${time}`;
      return `${d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" })} · ${time}`;
    } catch {
      return "";
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="h-8 px-2.5 sm:px-3 text-muted-foreground hover:text-rose-600 border-rose-200 hover:border-rose-300 hover:bg-rose-50"
        data-testid="home-voicenote-btn"
        title="Note vocali (promemoria rapidi)"
      >
        <Mic className="w-4 h-4 text-rose-500 sm:mr-1" />
        <span className="hidden sm:inline text-xs">Note vocali</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="home-voicenote-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mic className="w-4 h-4 text-rose-500" />
              Note vocali
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Recorder */}
            <div className="bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200 rounded-xl p-4">
              {processing ? (
                <div className="flex items-center justify-center gap-3 py-2">
                  <Loader2 className="w-5 h-5 animate-spin text-rose-500" />
                  <span className="text-sm">Trascrivo...</span>
                </div>
              ) : recording ? (
                <div className="flex flex-col items-center gap-3">
                  <button
                    onClick={stopRec}
                    className="w-16 h-16 rounded-full bg-red-500 animate-pulse flex items-center justify-center shadow-lg"
                    data-testid="home-voicenote-stop-btn"
                    aria-label="Invia nota vocale"
                  >
                    <Square className="w-6 h-6 text-white fill-white" />
                  </button>
                  <span className="text-sm font-mono text-red-600">
                    {Math.floor(elapsed / 60)}:{(elapsed % 60).toString().padStart(2, "0")}
                  </span>
                  <p className="text-xs text-muted-foreground">Tocca il quadrato per inviare</p>
                  <button
                    onClick={cancelRec}
                    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-red-600"
                    data-testid="home-voicenote-cancel-btn"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Cestina
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <button
                    onClick={startRec}
                    className="w-16 h-16 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition-transform"
                    data-testid="home-voicenote-record-btn"
                    aria-label="Inizia registrazione"
                  >
                    <Mic className="w-7 h-7 text-white" />
                  </button>
                  <p className="text-xs text-muted-foreground text-center">
                    Tocca il microfono e parla.<br />Verrà trascritta automaticamente.
                  </p>
                </div>
              )}
            </div>

            {/* Last transcription preview */}
            {lastResult && !recording && !processing && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 space-y-2" data-testid="home-voicenote-last-result">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-wider font-semibold text-green-700">
                  <span>Ultima nota trascritta</span>
                  <span className="font-normal text-green-600/60">{formatNoteTime(lastResult.recorded_at)}</span>
                </div>
                {lastResult.has_audio && (
                  <VoiceNoteAudio
                    noteId={lastResult.note_id}
                    url={audioUrls[lastResult.note_id]}
                    loading={loadingAudio[lastResult.note_id]}
                    onLoad={() => loadAudioFor(lastResult.note_id)}
                  />
                )}
                <p className="text-sm text-foreground leading-relaxed">{lastResult.transcript}</p>
                <div className="flex gap-2 pt-1">
                  <button
                    onClick={() => copyToClipboard(lastResult.transcript)}
                    className="text-[11px] text-green-700 hover:text-green-900 underline underline-offset-2"
                  >
                    Copia testo
                  </button>
                  <button
                    onClick={() => deleteNote(lastResult.note_id)}
                    className="text-[11px] text-red-600 hover:text-red-800 underline underline-offset-2"
                  >
                    Elimina
                  </button>
                </div>
              </div>
            )}

            {/* Archive toggle */}
            <div className="border-t pt-3">
              <button
                onClick={() => setShowArchive((v) => !v)}
                className="flex items-center justify-between w-full text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                data-testid="home-voicenote-archive-toggle"
              >
                <span className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  Archivio ({notes.length} not{notes.length === 1 ? "a" : "e"})
                </span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showArchive ? "rotate-180" : ""}`} />
              </button>

              {showArchive && (
                <div className="mt-3 space-y-2" data-testid="home-voicenote-archive">
                  {loadingNotes ? (
                    <div className="flex justify-center py-3">
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    </div>
                  ) : notes.length === 0 ? (
                    <p className="text-xs text-muted-foreground text-center py-4">
                      Nessuna nota ancora. Registrane una qui sopra!
                    </p>
                  ) : (
                    notes.map((n) => (
                      <div
                        key={n.note_id}
                        className="bg-white border rounded-lg p-2.5 space-y-1.5"
                        data-testid={`voicenote-item-${n.note_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-muted-foreground">{formatNoteTime(n.recorded_at)}</span>
                          <div className="flex gap-1">
                            <button
                              onClick={() => copyToClipboard(n.transcript)}
                              className="p-1 text-muted-foreground hover:text-foreground"
                              title="Copia"
                            >
                              <ClipboardList className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => deleteNote(n.note_id)}
                              className="p-1 text-muted-foreground hover:text-red-600"
                              title="Elimina"
                              data-testid={`delete-voicenote-${n.note_id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                        {n.audio_file_id && (
                          <VoiceNoteAudio
                            noteId={n.note_id}
                            url={audioUrls[n.note_id]}
                            loading={loadingAudio[n.note_id]}
                            onLoad={() => loadAudioFor(n.note_id)}
                          />
                        )}
                        <p className="text-xs leading-relaxed text-foreground">{n.transcript}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ===== MAIN PAGE =====
// =================== OGGI TILE (compact "today's activities" tile) ===================
// Replaces the old multi-widget Focus grid. Shows a single compact card with
// today's counts (tasks, calendar, reminders); tapping it opens a Dialog with
// the full list. Keeps the Home Revela-centric while preserving quick access
// to the day's organizational items.
// =================== DISCOVERIES CARD ("Le mie scoperte") ===================
// Progressively reveals indicators as the user accumulates check-ins. Shows:
//   • "few check-ins" empty state with the next milestone
//   • a list of unlocked + locked indicators with progress
//   • a single CTA to the full dashboard on the Benessere page
const DiscoveriesCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get("/voice-mood/discoveries-summary")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Card className="border border-slate-200 bg-white" data-testid="discoveries-card-loading">
        <CardContent className="p-4 sm:p-5">
          <div className="h-5 w-32 bg-slate-100 rounded animate-pulse" />
          <div className="h-3 w-48 bg-slate-100 rounded mt-2 animate-pulse" />
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const { n_total, indicators, unlocked_count, total_indicators, next_unlock } = data;
  const visibleIndicators = expanded ? indicators : indicators.slice(0, 4);

  return (
    <Card className="border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow" data-testid="discoveries-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0">
              <Sparkles className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <p className="font-display text-2xl sm:text-3xl text-slate-800 font-light leading-tight">Le mie scoperte</p>
              <p className="text-xs text-slate-500 font-light mt-0.5">
                {n_total === 0
                  ? "Inizia con il primo check-in"
                  : `${n_total} ${n_total === 1 ? "check-in" : "check-in"} · ${unlocked_count}/${total_indicators} scoperte attive`}
              </p>
            </div>
          </div>
          {unlocked_count > 0 && (
            <Link
              to="/benessere?section=checkin-benessere&tab=dashboard"
              className="hidden sm:flex items-center gap-1 px-3 py-2 rounded-full border border-slate-200 bg-white text-xs font-light text-slate-600 hover:bg-slate-50 hover:border-slate-300 shrink-0 transition-colors"
              data-testid="discoveries-open-dashboard"
            >
              Apri
              <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          )}
        </div>

        {/* Empty / few-data state */}
        {unlocked_count === 0 && next_unlock && (
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid="discoveries-empty-state">
            <p className="text-sm text-slate-700 font-light leading-relaxed">
              Aumenta il numero di check-in per vedere i tuoi risultati.
            </p>
            <p className="text-xs text-slate-500 font-light mt-1.5">
              La prima scoperta — <span className="italic">{next_unlock.label}</span> — si sblocca a {next_unlock.min_n} {next_unlock.threshold_unit}.
              <span className="text-slate-400"> Sei a {next_unlock.progress}/{next_unlock.min_n}.</span>
            </p>
          </div>
        )}

        {/* Per-indicator list (progressive disclosure) */}
        {unlocked_count > 0 && (
          <>
            <ul className="space-y-2" data-testid="discoveries-list">
              {visibleIndicators.map((ind) => {
                const pct = Math.round((ind.progress / ind.min_n) * 100);
                return (
                  <li
                    key={ind.key}
                    className={`flex items-center gap-3 ${ind.unlocked ? "" : "opacity-60"}`}
                    data-testid={`discoveries-item-${ind.key}`}
                  >
                    <div className={`w-2 h-2 rounded-full shrink-0 ${ind.unlocked ? "bg-slate-700" : "bg-slate-300"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm text-slate-700 font-light truncate">{ind.label}</p>
                        <span className="text-[10px] text-slate-400 font-light tracking-wide shrink-0">
                          {ind.unlocked ? "Disponibile" : `${ind.progress}/${ind.min_n}`}
                        </span>
                      </div>
                      {!ind.unlocked && (
                        <div className="h-0.5 bg-slate-100 rounded-full mt-1.5 overflow-hidden">
                          <div className="h-full bg-slate-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
            {indicators.length > 4 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 mt-3 text-xs text-slate-500 hover:text-slate-700 font-light tracking-wide transition-colors"
                data-testid="discoveries-toggle-more"
              >
                {expanded ? "Mostra meno" : `Mostra tutte (${indicators.length})`}
                <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} strokeWidth={1.5} />
              </button>
            )}
            {/* Mobile-only "Apri" button */}
            <Link
              to="/benessere?section=checkin-benessere&tab=dashboard"
              className="sm:hidden flex items-center justify-center gap-1 mt-3 py-2 rounded-full border border-slate-200 bg-white text-xs font-light text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="discoveries-open-dashboard-mobile"
            >
              Apri tutte le scoperte
              <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </Link>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const OggiTile = ({ data, todayStr }) => {
  const [open, setOpen] = useState(false);

  const tasks = data?.tasks?.urgent_priority || [];
  const events = data?.calendar?.today || [];
  const reminders = data?.reminders?.today || [];
  const total = tasks.length + events.length + reminders.length;

  const formatTime = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full text-left active:scale-[0.99] transition-transform h-full"
        data-testid="oggi-tile"
      >
        <Card className="border border-slate-200 hover:border-slate-300 bg-white shadow-sm hover:shadow-md transition-all h-full">
          <CardContent className="p-4 flex items-center gap-3 h-full">
            <div className="w-11 h-11 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center shrink-0">
              <Calendar className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display text-xl text-slate-800 font-light leading-tight">Oggi</p>
              <p className="text-xs text-slate-500 font-light mt-0.5">
                {total === 0
                  ? "Nessuna attività in programma"
                  : `${total} ${total === 1 ? "elemento" : "elementi"}`}
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" strokeWidth={1.5} />
          </CardContent>
        </Card>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md p-0 max-h-[85vh] overflow-y-auto" data-testid="oggi-tile-dialog">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Cosa hai oggi
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 space-y-5">
            {total === 0 && (
              <div className="text-center py-6">
                <Sparkles className="w-7 h-7 text-muted-foreground/40 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Niente in programma per oggi.</p>
              </div>
            )}

            {tasks.length > 0 && (
              <div data-testid="oggi-section-tasks">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <ClipboardList className="w-3.5 h-3.5" />
                  Urgenze ({tasks.length})
                </p>
                <ul className="space-y-1.5">
                  {tasks.slice(0, 8).map((t) => (
                    <li key={t.id || t._id} className="flex items-start gap-2 text-sm">
                      <Check className="w-3.5 h-3.5 text-blue-500 mt-0.5 shrink-0" />
                      <span className="flex-1 min-w-0 break-words">{t.title}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/organizza?section=compiti" className="text-xs text-blue-600 hover:underline mt-2 inline-block">
                  Apri Compiti →
                </Link>
              </div>
            )}

            {events.length > 0 && (
              <div data-testid="oggi-section-events">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  Appuntamenti ({events.length})
                </p>
                <ul className="space-y-1.5">
                  {events.slice(0, 8).map((e) => (
                    <li key={e.id || e._id} className="flex items-start gap-2 text-sm">
                      <span className="font-mono text-xs text-purple-600 mt-0.5 shrink-0">
                        {formatTime(e.start_time || e.datetime || e.date)}
                      </span>
                      <span className="flex-1 min-w-0 break-words">{e.title}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/organizza?section=calendario" className="text-xs text-purple-600 hover:underline mt-2 inline-block">
                  Apri Calendario →
                </Link>
              </div>
            )}

            {reminders.length > 0 && (
              <div data-testid="oggi-section-reminders">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5">
                  <Bell className="w-3.5 h-3.5" />
                  Promemoria ({reminders.length})
                </p>
                <ul className="space-y-1.5">
                  {reminders.slice(0, 8).map((r) => (
                    <li key={r.id || r._id} className="flex items-start gap-2 text-sm">
                      <span className="font-mono text-xs text-amber-600 mt-0.5 shrink-0">
                        {formatTime(r.reminder_time || r.datetime)}
                      </span>
                      <span className="flex-1 min-w-0 break-words">{r.title}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const OggiPage = ({ user }) => {
  const [loading, setLoading] = useState(true);
  const [focusData, setFocusData] = useState(null);
  const [moodSubmitting, setMoodSubmitting] = useState(false);
  const [showFocusSettings, setShowFocusSettings] = useState(false);
  const [hasRevelaAccess, setHasRevelaAccess] = useState(null); // null=unknown, true/false=loaded

  const [selectedFocus, setSelectedFocus] = useState(() => {
    const saved = localStorage.getItem(FOCUS_PREF_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed.slice(0, MAX_FOCUS_ITEMS);
      } catch { /* falls back to DEFAULT_FOCUS below */ }
    }
    return DEFAULT_FOCUS;
  });

  const today = new Date();
  const todayStr = today.toISOString().split("T")[0];

  const greeting = useMemo(() => {
    const hour = today.getHours();
    if (hour < 12) return "Buongiorno";
    if (hour < 18) return "Buon pomeriggio";
    return "Buonasera";
  }, [today]);

  const formattedDate = today.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  useEffect(() => {
    loadFocusData();
    // Check Revela whitelist gating (mirror of /api/voice-mood/access)
    api.get("/voice-mood/access")
      .then((r) => setHasRevelaAccess(!!r.data?.has_access))
      .catch(() => setHasRevelaAccess(false));
  }, []);

  const loadFocusData = async () => {
    setLoading(true);
    try {
      const res = await api.get("/dashboard/oggi-focus");
      setFocusData(res.data);
    } catch (err) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const toggleFocusItem = (focusId) => {
    setSelectedFocus(prev => {
      let next;
      if (prev.includes(focusId)) {
        next = prev.filter(id => id !== focusId);
      } else if (prev.length < MAX_FOCUS_ITEMS) {
        next = [...prev, focusId];
      } else {
        toast.error(`Puoi selezionare massimo ${MAX_FOCUS_ITEMS} focus`);
        return prev;
      }
      localStorage.setItem(FOCUS_PREF_KEY, JSON.stringify(next));
      return next;
    });
  };

  const handleQuickMood = async (level) => {
    setMoodSubmitting(true);
    try {
      await api.post("/moods", { date: todayStr, level, note: "" });
      setFocusData(prev => prev ? { ...prev, mood: { today: { level } } } : prev);
      toast.success("Umore registrato!");
    } catch {
      toast.error("Errore nel salvare l'umore");
    } finally {
      setMoodSubmitting(false);
    }
  };

  const focusConfigs = useMemo(
    () => selectedFocus.map(id => ALL_FOCUS_OPTIONS.find(o => o.id === id)).filter(Boolean),
    [selectedFocus]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]" data-testid="oggi-loading">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // First name with capital initial (e.g. "Laura" from "laura piccinini")
  const firstName = user?.name
    ? user.name.split(" ")[0].charAt(0).toUpperCase() + user.name.split(" ")[0].slice(1).toLowerCase()
    : "";
  // eslint-disable-next-line no-unused-vars
  const selfPhrase = selfDiscoveryPhrase(user?.name);
  const readyPhrase = readyToDiscoverPhrase(user?.name);

  return (
    <div className="space-y-6 sm:space-y-8 animate-fade-in pb-24 md:pb-8 max-w-2xl md:max-w-4xl mx-auto px-1 sm:px-0" data-testid="oggi-page">
      {/* ===== HERO ===== */}
      <div className="pt-4 sm:pt-6" data-testid="oggi-hero">
        <p className="font-display text-3xl sm:text-4xl text-slate-800 font-light leading-none" data-testid="hero-greeting">
          Ciao{firstName ? `, ${firstName}` : ""}
        </p>
        {hasRevelaAccess === true && (
          <p className="font-display text-base sm:text-lg text-slate-500 italic font-light mt-1.5" data-testid="hero-self-discovery">
            {readyPhrase}
          </p>
        )}
        <p className="text-xs text-slate-400 capitalize mt-3 tracking-wide font-light">{formattedDate}</p>
      </div>

      {/* ===== CHECK-IN CTA + LE MIE SCOPERTE (whitelisted users only) ===== */}
      {hasRevelaAccess === true && (
        <>
          <HomeCheckInLauncher />
          <DiscoveriesCard />
        </>
      )}

      {/* ===== OGGI TILE ===== */}
      <OggiTile data={focusData} todayStr={todayStr} />

      {/* ===== ALTRE FUNZIONI (Organizza · Benessere · Percorsi) ===== */}
      <div className="pt-4">
        <p className="text-[10px] sm:text-xs font-light text-slate-400 uppercase tracking-[0.25em] mb-3" data-testid="altre-funzioni-label">
          Altre funzioni
        </p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Organizza", icon: ClipboardList, path: "/organizza" },
            { label: "Benessere", icon: Heart, path: "/benessere" },
            { label: "Percorsi", icon: Compass, path: "/percorsi" },
          ].map(hub => (
            <Link key={hub.path} to={hub.path} data-testid={`hub-${hub.label.toLowerCase()}`}>
              <div className="flex flex-col items-center gap-2 py-4 px-3 rounded-xl bg-white border border-slate-200 hover:border-slate-400 hover:shadow-sm transition-all group">
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                  <hub.icon className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" strokeWidth={1.5} />
                </div>
                <span className="text-xs font-light text-slate-700 tracking-wide">{hub.label}</span>
              </div>
            </Link>
          ))}
        </div>

        {/* Voice tools (admin only) — silver rectangles, centered below the hubs */}
        {user?.email === ADMIN_EMAIL && (
          <div className="mt-4 flex justify-center gap-3" data-testid="voice-tools-wrapper">
            <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-slate-100 to-slate-50 px-3 py-2 hover:border-slate-300 transition-colors" data-testid="voice-tool-magic">
              <SmartVoiceCommand />
            </div>
            <div className="rounded-xl border border-slate-200 bg-gradient-to-b from-slate-100 to-slate-50 px-3 py-2 hover:border-slate-300 transition-colors" data-testid="voice-tool-recorder">
              <HomeVoiceRecorder />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OggiPage;
